import { SearchOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Company,
  ErrorResponse,
  GET_COMPANY,
  GET_TOPICS,
  PATCH_COMPANY,
  Topic,
} from '@frontend/data-access';
import { TagPicker } from '@frontend/shared/ui';
import { App, Button, Form, Input, Spin } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';

interface FormValues {
  description: string;
}

function EditIndustriesForm({
  companyId,
  onFinish,
  onCancel,
}: {
  companyId: number;
  onFinish: () => void;
  onCancel: () => void;
}) {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const { data, loading } = useQuery<{ company: Company }>(GET_COMPANY, {
    variables: { id: companyId },
  });
  const [isIncomplete, setIsIncomplete] = useState(false);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [form] = Form.useForm();
  const { data: dataTopics } = useQuery<{
    topics: Topic[];
  }>(GET_TOPICS);

  const handleCompleted = () => {
    notification.success({
      message: t('successfully_saved'),
    });
    onFinish();
  };

  const handleError = (data: ErrorResponse) => {
    notification.error({
      message: t('error'),
      description: data.message,
    });
  };

  const [patchCompany, { loading: loadingPatch }] = useMutation(PATCH_COMPANY, {
    onCompleted: handleCompleted,
    onError: handleError,
  });

  const handleOnFinish = (values: FormValues) => {
    patchCompany({
      variables: {
        input: {
          id: companyId,
          ...values,
        },
      },
    });
  };

  const handleFormChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    setIsIncomplete(hasErrors);
  };

  const filterByName = (tagName: string) => {
    setTopics(
      dataTopics?.topics.filter((tag) => {
        return tag.name.toLowerCase().indexOf(tagName.toLowerCase()) === 0;
      }) || []
    );
  };

  useEffect(() => {
    if (dataTopics?.topics.length) {
      setTopics(dataTopics.topics);
    }
  }, [dataTopics]);

  if (loading) {
    return (
      <div className="text-center p-6">
        <Spin />
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFieldsChange={handleFormChange}
      onFinish={handleOnFinish}
    >
      <div className="text-center">
        <h2 className="text-3xl font-medium">{t('institution_industry')}</h2>
      </div>
      <div className="flex justify-center mb-4">
        <Input
          data-test="interested-search"
          className="max-w-[463px]"
          prefix={<SearchOutlined />}
          placeholder={t('search')}
          onChange={(e) => filterByName(e.target.value)}
        />
      </div>
      <Form.Item
        name="topicIds"
        label={t('industry')}
        rules={[{ required: true, message: t('select_industries') }]}
      >
        <TagPicker
          defaultValues={data?.company.topics?.map((i) => i.id)}
          options={topics.map((i) => ({
            value: i.id,
            label: i.name,
          }))}
          onChange={(value: any[]) => form.setFieldValue('topicIds', value)}
        />
      </Form.Item>
      <div className="flex justify-end items-center gap-2">
        <Button disabled={loadingPatch} shape="round" onClick={onCancel}>
          {t('cancel')}
        </Button>
        <Button
          htmlType="submit"
          type="primary"
          loading={loadingPatch}
          disabled={isIncomplete}
          shape="round"
          data-test="industries-save-btn"
        >
          {t('save')}
        </Button>
      </div>
    </Form>
  );
}

export default EditIndustriesForm;
